import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';

/**
 * Custom hook for wallet balance management
 * Provides balance fetching, validation, and automatic refresh functionality
 */
export const useWalletBalance = (walletId = null) => {
  const [balance, setBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const { tokens } = useAuth();
  const { activeWallet } = useWallet();
  
  // Use provided walletId or fall back to active wallet
  const targetWalletId = walletId || activeWallet?.id;
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  }, [tokens, API_BASE_URL]);

  /**
   * Fetch wallet balance from API
   */
  const fetchBalance = useCallback(async (detailed = true, forceRefresh = false) => {
    if (!targetWalletId) {
      setBalance(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const endpoint = forceRefresh 
        ? `/wallets/${targetWalletId}/refresh-balance`
        : `/wallets/${targetWalletId}/balance${detailed ? '?detailed=true' : ''}`;
      
      const method = forceRefresh ? 'POST' : 'GET';
      
      const data = await makeAuthenticatedRequest(endpoint, { method });
      
      setBalance(data.data);
      setLastUpdated(new Date().toISOString());
      
      console.log('✅ Balance updated:', {
        walletId: targetWalletId,
        sol: data.data.sol?.balance || data.data.sol,
        tokenCount: data.data.tokens?.length || Object.keys(data.data.tokens || {}).length,
        forceRefresh
      });
      
      return data.data;
    } catch (error) {
      console.error('❌ Failed to fetch balance:', error.message);
      setError(error.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [targetWalletId, makeAuthenticatedRequest]);

  /**
   * Validate if wallet has sufficient balance for a transaction
   */
  const validateBalance = useCallback(async (tokenMint, amount, includeFees = true) => {
    if (!targetWalletId) {
      throw new Error('No wallet selected');
    }

    try {
      const data = await makeAuthenticatedRequest(`/wallets/${targetWalletId}/validate-balance`, {
        method: 'POST',
        body: JSON.stringify({
          token_mint: tokenMint,
          amount: amount,
          include_fees: includeFees
        })
      });

      return data.data.validation;
    } catch (error) {
      console.error('❌ Balance validation failed:', error.message);
      throw error;
    }
  }, [targetWalletId, makeAuthenticatedRequest]);

  /**
   * Get available balance for a specific token
   */
  const getTokenBalance = useCallback((tokenMint) => {
    if (!balance) return 0;

    if (tokenMint === 'So11111111111111111111111111111111111111112' || !tokenMint) {
      // SOL balance - ensure we return a number
      const solBalance = balance.sol?.available_for_trading || balance.sol?.balance || balance.sol || 0;
      return typeof solBalance === 'number' ? solBalance : parseFloat(solBalance) || 0;
    } else {
      // SPL Token balance
      if (balance.tokens) {
        if (Array.isArray(balance.tokens)) {
          const token = balance.tokens.find(t => t.mint === tokenMint);
          const tokenBalance = token ? token.balance : 0;
          return typeof tokenBalance === 'number' ? tokenBalance : parseFloat(tokenBalance) || 0;
        } else {
          const tokenBalance = balance.tokens[tokenMint]?.balance || 0;
          return typeof tokenBalance === 'number' ? tokenBalance : parseFloat(tokenBalance) || 0;
        }
      }
      return 0;
    }
  }, [balance]);

  /**
   * Check if sufficient balance exists for a transaction
   */
  const hasSufficientBalance = useCallback((tokenMint, amount) => {
    const available = getTokenBalance(tokenMint);
    return available >= amount;
  }, [getTokenBalance]);

  /**
   * Format balance for display
   */
  const formatBalance = useCallback((tokenMint, decimals = 6) => {
    const balance = getTokenBalance(tokenMint);
    
    // Ensure balance is a valid number
    const numBalance = typeof balance === 'number' ? balance : parseFloat(balance) || 0;
    
    if (numBalance === 0) return '0';
    if (numBalance < 0.000001) return '< 0.000001';
    
    return numBalance.toFixed(decimals);
  }, [getTokenBalance]);

  /**
   * Refresh balance (force fetch from blockchain)
   */
  const refreshBalance = useCallback(async () => {
    return await fetchBalance(true, true);
  }, [fetchBalance]);

  // Auto-fetch balance when wallet changes
  useEffect(() => {
    if (targetWalletId && tokens) {
      fetchBalance(true, false);
    }
  }, [targetWalletId, tokens, fetchBalance]);

  // Auto-refresh balance every 2 minutes for active wallet (reduced to avoid RPC rate limits)
  useEffect(() => {
    if (!targetWalletId || !tokens) return;

    const interval = setInterval(() => {
      fetchBalance(true, false);
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, [targetWalletId, tokens, fetchBalance]);

  return {
    // State
    balance,
    isLoading,
    error,
    lastUpdated,
    
    // Actions
    fetchBalance,
    refreshBalance,
    validateBalance,
    
    // Utilities
    getTokenBalance,
    hasSufficientBalance,
    formatBalance,
    
    // Computed values
    solBalance: (() => {
      const sol = balance?.sol?.balance || balance?.sol || 0;
      return typeof sol === 'number' ? sol : parseFloat(sol) || 0;
    })(),
    availableSolForTrading: (() => {
      const available = balance?.sol?.available_for_trading;
      if (available !== undefined) {
        return typeof available === 'number' ? available : parseFloat(available) || 0;
      }
      const sol = balance?.sol?.balance || balance?.sol || 0;
      const solNum = typeof sol === 'number' ? sol : parseFloat(sol) || 0;
      return Math.max(0, solNum - 0.01);
    })(),
    tokenCount: balance?.tokens ? (Array.isArray(balance.tokens) ? balance.tokens.length : Object.keys(balance.tokens).length) : 0,
    hasBalance: (() => {
      const sol = balance?.sol?.balance || balance?.sol || 0;
      const solNum = typeof sol === 'number' ? sol : parseFloat(sol) || 0;
      return solNum > 0 || (balance?.tokens && Object.keys(balance.tokens || {}).length > 0);
    })()
  };
};

export default useWalletBalance;